import { cloneDeep, orderBy } from 'lodash'

import { AnalyticsField, DashboardsViews, DashboardWidgetName, Filter, FilterParam, PivotTableFields, PivotTableGroup, Sort } from '@types'

import { ASSET_FIELD, ASSET_ID_PREFIX } from '@/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'

const mapDynamicFilterParam = (param: FilterParam, assetsBunchStore: ReturnType<typeof useAssetsBunchStore>) => {
  let mappedParam = cloneDeep(param)
  if (`${param.value}`.startsWith(ASSET_ID_PREFIX)) {
    const asset = assetsBunchStore.getElementById(`${param.value}`)
    const name = asset?.field<string>(ASSET_FIELD.NAME).value
    if (name) {
      mappedParam.value = name
    }
  }
  return mappedParam
}

const mapFilterParams = (filter: Filter | undefined) => {
  const assetsBunchStore = useAssetsBunchStore()
  let updated = false
  const params = filter?.params?.map(param => {
    const params = param.params.map(param => {
      switch (param.field) {
        case AnalyticsField.ACCOUNT:
          updated = true
          return {
            ...mapDynamicFilterParam(param, assetsBunchStore),
            field: AnalyticsField.ACCOUNT_NAME
          }
        case AnalyticsField.ACCOUNT_ID:
          updated = true
          return {
            ...param,
            field: AnalyticsField.ACCOUNT_NAME
          }
        case AnalyticsField.ASSET_ID:
          updated = true
          return {
            ...param,
            field: AnalyticsField.ASSET_NAME
          }
        case AnalyticsField.ASSET:
          updated = true
          return {
            ...mapDynamicFilterParam(param, assetsBunchStore),
            field: AnalyticsField.ASSET_NAME
          }
        case AnalyticsField.CATEGORY_ACCOUNT_ID:
          updated = true
          return {
            ...param,
            field: AnalyticsField.CATEGORY_ACCOUNT_NAME
          }
      }
      return param
    })
    return {
      ...param,
      params
    }
  })
  return { updated, params }
}

export default async (dashboardViews: DashboardsViews[]) => {
  return dashboardViews.map(dashboardView => {
    let isUpdated = false
    const widgets = dashboardView.widgets?.map(widget => {
      if ([
        DashboardWidgetName.ValuePivot,
        DashboardWidgetName.IncomePivot,
        DashboardWidgetName.PerformancePivot,
      ].includes(widget.name)) {
        widget.attrs.configs = (widget.attrs.configs ?? []).map((config: any) => {
          let mappedConfig = cloneDeep(config)
          if (!!mappedConfig.filter) {
            const { updated, params } = mapFilterParams(config.filter)
            isUpdated ||= updated
            mappedConfig = {
              ...mappedConfig,
              filter: {
                ...(mappedConfig.filter || {}),
                params
              }
            }
          }
          if (mappedConfig.sortColumns?.length) {
            const valuesFields = Object.entries((mappedConfig.fields as PivotTableFields))?.filter(([, field]) => field.group === PivotTableGroup.VALUES)
            const valueField = orderBy(valuesFields, ([, field]) => field.index)?.at(0)?.at(0)
            if (valueField) {
              isUpdated = true
              mappedConfig.sortColumns = mappedConfig.sortColumns.map((item: Sort) => {
                if (item.field !== 'footer_grand_total') return item
                return {
                  ...item,
                  field: valueField,
                }
              })
            }
          }
          if (mappedConfig.sortRows?.length) {
            const colsFields = Object.entries((mappedConfig.fields as PivotTableFields))?.filter(([, field]) => field.group === PivotTableGroup.COLS)
            const colField = orderBy(colsFields, ([, field]) => field.index)?.at(0)
            isUpdated = true
            mappedConfig.sortRows = mappedConfig.sortRows.map((item: Sort) => {
              try {
                // check if item is old value
                const field = JSON.parse(item.field)
                if (Array.isArray(field)) throw new Error()
                // check old grand total sort rule
                if (field.grand_total)  {
                  const valueField = Object.keys(field).filter(key => key !== 'grand_total')?.at(-1) || ''
                  return {
                    ...item,
                    field: valueField,
                  }
                } else {
                  let fieldKey = Object.entries(field)
                  fieldKey = orderBy(fieldKey, ([key]) => {
                    const index = colField?.indexOf(`${key}`)
                    return index === -1 ? Infinity : index
                  })
                  fieldKey.at(-1)![1] = fieldKey.at(-1)![0]
                  return {
                    ...item,
                    field: JSON.stringify(fieldKey),
                  }
                }
              } catch {
                return item
              }
            })
          }
          return mappedConfig
        })
        return widget
      } else if (!!widget.attrs.filter) {
        const { updated, params } = mapFilterParams(widget.attrs.filter)
        isUpdated ||= updated
        return {
          ...widget,
          attrs: {
            ...widget.attrs,
            filter: {
              ...(widget.attrs.filter || {}),
              params
            }
          },
        }
      }
      return widget
    })
    return isUpdated ? { ...dashboardView, widgets } : dashboardView
  })
}
